import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CONTACT_INFO } from '../config/constants';
import { NavbarImageStatic, NavbarImageWhenScrolled } from '../data/photos';

export default function Navbar() {
  const phone = CONTACT_INFO.mainPhone;
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(window.scrollY);
  const [removeBoxShadow, setRemoveBoxShadow] = useState(false);
  const [scrollY, setScrollY] = useState(window.scrollY);
  const [scrolled, setScrolled] = useState(false); // track if scrolled

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) return;

      const currentScrollY = window.scrollY;
      setScrollY(currentScrollY);
      setRemoveBoxShadow(currentScrollY > 4);
      setShowNavbar(!(currentScrollY > lastScrollY && currentScrollY > 48));
      setLastScrollY(currentScrollY);

      setScrolled(currentScrollY > 0); // true if scrolled down
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, isOpen]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => (document.body.style.overflow = '');
  }, [isOpen]);

  const outerClass = [
    'fixed w-full z-50 transition-all duration-300 ease-in-out',
    showNavbar ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none',
    scrollY <= 4 ? 'mt-0' : '-mt-14 md:-mt-6',
  ].join(' ');

  const innerClass = [
    'mt-3 mx-4 md:mx-4 sm:px-8 rounded-lg md:rounded-xl bg-white backdrop-blur-[32px]',
    'flex px-6 h-[56px] items-center justify-between',
    removeBoxShadow
      ? 'border border-gray-200 shadow-none'
      : 'border-b border-gray-200 shadow-[0_2px_14px_0_rgba(0,0,0,0.06)]',
  ].join(' ');

  return (
    <header role="banner" className={outerClass}>
      <nav className={innerClass} role="navigation" aria-label="Main navigation">
        {/* Brand Logo with fade */}
        <Link to="/" className="flex items-center select-none justify-start" aria-label="Go to home page">
         <div className="relative h-24 w-72 flex items-center justify-start">
          <img
            src={NavbarImageStatic}
            alt="Madeshwara Ambulance logo"
            className={`absolute left-0 top-0 h-24 w-auto mr-2 -ml-2 transition-opacity duration-500 ${scrolled ? 'opacity-0' : 'opacity-100'}`}
            loading="lazy"
            decoding="async"
          />
          <img
            src={NavbarImageWhenScrolled}
            alt="Madeshwara Ambulance logo scrolled"
            className={`absolute h-24 w-auto mr-2 -ml-2 transition-opacity duration-500 ${scrolled ? 'opacity-100' : 'opacity-0'}`}
            loading="lazy"
            decoding="async"
          />
          </div>
        </Link>

        {/* Desktop Menu */}
        <ul className="hidden lg:flex space-x-8 items-center text-gray-900 font-medium">
          {[{ to: '/', label: 'Home' }, { to: '/about', label: 'About Us' }, { to: '/services', label: 'Services' }, { to: '/contact', label: 'Contact Us' }].map((item) => (
            <li key={item.to}>
              <Link
                to={item.to}
                className={`transition tracking-wide ${isActive(item.to) ? 'text-sky-700 font-semibold' : 'hover:text-sky-700'}`}
                aria-current={isActive(item.to) ? 'page' : undefined}
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>

        {/* Hamburger Menu */}
        <button
          onClick={() => setIsOpen(true)}
          className="lg:hidden text-sky-600 focus:outline-none p-2 w-10 h-10 flex items-center justify-center"
          aria-label="Open menu"
        >
          <span className="text-2xl">&#9776;</span>
        </button>

        {/* Mobile Menu */}
        <aside
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation menu"
          className={`fixed top-0 right-0 h-screen w-[92vw] bg-white backdrop-blur-lg shadow-lg px-4 pt-7 
            pb-4 flex flex-col space-y-4 lg:hidden z-50 rounded-l-3xl border border-gray-200 
            transition-transform duration-300 ease-in-out ${
            isOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'
          }`}
          // className={`fixed  inset-0 h-screen w-[92vw] bg-white backdrop-blur-lg shadow-lg 
          //   px-6 pt-8 pb-6 flex flex-col space-y-4 lg:hidden z-[9999]
          //   transition-transform duration-300 ease-in-out overflow-y-auto ${
          //     isOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'
          //   }`}
        >
          <div className="flex items-center justify-between w-full">
            {/* Mobile logo fade */}
            <div className="relative w-36 h-14 flex items-start justify-start">
              <img
                src={NavbarImageStatic}
                alt="logo"
                className={`absolute left-0 top-0 h-12 w-auto transition-opacity duration-500 ${scrolled ? 'opacity-0' : 'opacity-100'}`}
                style={{ zIndex: 2 }}
              />
              <img
                src={NavbarImageWhenScrolled}
                alt="logo scrolled"
                className={`absolute left-0 top-0 h-12 w-auto transition-opacity duration-500 ${scrolled ? 'opacity-100' : 'opacity-0'}`}
                style={{ zIndex: 1 }}
              />
            </div>


            <button
              onClick={() => setIsOpen(false)}
              className="text-sky-600 focus:outline-none p-2 w-10 h-10 flex items-center justify-center rounded-full border border-sky-600 bg-white shadow"
              aria-label="Close menu"
            >
              <span className="text-2xl">✖</span>
            </button>
          </div>

          <ul className="pt-2 w-full">
            {[{ to: '/', label: 'Home' }, { to: '/about', label: 'About Us' }, { to: '/services', label: 'Services' }, { to: '/contact', label: 'Contact Us' }].map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  onClick={() => setIsOpen(false)}
                  className={`text-lg p-3 rounded transition border-b border-gray-200 font-medium tracking-wide block ${
                    isActive(item.to) ? 'text-sky-700 font-semibold' : 'text-gray-700 hover:text-sky-600'
                  }`}
                  aria-current={isActive(item.to) ? 'page' : undefined}
                >
                  {item.label.toUpperCase()}
                </Link>
              </li>
            ))}
          </ul>

          <a
            href={`tel:${phone}`}
            className="bg-sky-600 px-5 py-3 rounded-full text-white font-semibold shadow mt-auto flex justify-center tracking-wide hover:bg-sky-700 transition"
            onClick={() => setIsOpen(false)}
            aria-label={`Call Madeshwara Ambulance at ${phone}`}
          >
            🚨 Call: 0{phone}
          </a>
        </aside>
      </nav>
    </header>
  );
}
